import { ProductOrderItem } from 'types/api/orders.type';

export type StyleGroup = {
  /** Composite key: `${productCode}-${mainFabricCode}`. */
  styleKey: string;
  /** Product code (replaces legacy `itemCode`). */
  productCode: string;
  mainFabricCode: string;
  colours: ProductOrderItem[];
};

/**
 * Groups order items by style — a style is one (productCode, mainFabricCode)
 * pair. Each group's `colours` array contains one entry per colour variant.
 *
 * Property 1 (validated in tests): output length equals unique style key count,
 * and every input item appears in exactly one group.
 *
 * `previous` is the result of the last call: a group whose colours are the same
 * item objects as before is returned as that same object, so memoized rows of
 * untouched styles skip re-rendering when one item of a large order changes.
 */
export function groupByStyle(
  items: ProductOrderItem[],
  previous: readonly StyleGroup[] = []
): StyleGroup[] {
  const map = new Map<string, StyleGroup>();

  for (const item of items) {
    const productCode = item.productCode ?? item.itemCode ?? '';
    const key = `${productCode}-${item.mainFabricCode}`;

    if (!map.has(key)) {
      map.set(key, {
        styleKey: key,
        productCode,
        mainFabricCode: item.mainFabricCode,
        colours: [],
      });
    }

    map.get(key)!.colours.push(item);
  }

  const previousByKey = new Map(previous.map((group) => [group.styleKey, group]));

  return Array.from(map.values(), (group) => {
    const prev = previousByKey.get(group.styleKey);
    const unchanged =
      prev !== undefined &&
      prev.productCode === group.productCode &&
      prev.mainFabricCode === group.mainFabricCode &&
      prev.colours.length === group.colours.length &&
      prev.colours.every((colour, index) => colour === group.colours[index]);
    return unchanged ? prev : group;
  });
}
